import clsx from "clsx";
import React from "react";
import { useDispatch, useSelector } from "react-redux";

import AssessmentIcon from "@mui/icons-material/Assessment";
import AssignmentIcon from "@mui/icons-material/Assignment";
import SignedReportIcon from "@mui/icons-material/AssignmentTurnedIn";
import FavoriteIcon from "@mui/icons-material/Favorite";
import ReportTemplateIcon from "@mui/icons-material/LibraryBooks";
import BillingIcon from "@mui/icons-material/MonetizationOnRounded";
import NoteAddIcon from "@mui/icons-material/NoteAdd";
import PeopleIcon from "@mui/icons-material/People";
import PieChartIcon from "@mui/icons-material/PieChart";
import PublishIcon from "@mui/icons-material/Publish";
import SettingsIcon from "@mui/icons-material/Settings";
import TableChartIcon from "@mui/icons-material/TableChart";
import GppGoodIcon from "@mui/icons-material/GppGood";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";

import { Drawer, List } from "@mui/material";
import { makeStyles } from "@mui/styles";

import WearableIcon from "components/common/icons/WearableIcon";
import { changeFilters } from "app/store/patient/slice/patientSlice";
import { removeCache } from "app/utils/cache";
import { USER_ROLES, CLINIC_NAMES, CLINIC_ROLES } from "static/constants";
import SideNavTab from "./components/SideNavTab";
import FileAlertIcon from "components/pages/nonCompliantPatients/icon/FileAlertIcon";
import useCheckRPMSaaSAccountUser from "hooks/useCheckRPMSaaSAccountUser";
import { getRolePreferenceByAccount } from "utils/accountPreference";
import { checkQuickTextTemplateAccess } from "utils/account.util";

const drawerWidth = 240;

const useStyles = makeStyles((theme) => ({
  drawer: {
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: "nowrap",
  },
  drawerOpen: {
    width: drawerWidth,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  drawerClose: {
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: "hidden",
    width: theme.spacing(7),
    [theme.breakpoints.down("sm")]: {
      width: theme.spacing(9) + 1,
    },
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    padding: theme.spacing(0, 1),
    ...theme.mixins.toolbar,
  },
  button: {
    color: theme.palette.primary.light,
  },
}));

export default function Sidenav(props) {
  const classes = useStyles();
  const { open, role, clinicName } = props;
  const dispatch = useDispatch();

  const { editPreference } = useSelector((state) => state.accountPreference);
  const { editPreference: editNotificationPreference } = useSelector(
    (state) => state.notificationPreference
  );

  const hasRPMDevices =
    JSON.parse(localStorage.getItem("hasRPMDevices")) ?? false;

  const transmissionsRoute =
    role === "clinic" ? "transmissions/clinic" : "transmissions";

  const { currentUser } = useSelector((state) => state.auth);
  const { accounts, accountsMapper } = useSelector((state) => state.account);
  const { transmissionCount } = useSelector((state) => state.transmission);
  const hasSettingsAccess = checkQuickTextTemplateAccess(accounts, currentUser);

  let activeCount =
    role === "clinic" ? transmissionCount.clinic : transmissionCount.active;

  const disableTab = editPreference || editNotificationPreference;

  const isRPMSaaSUser = useCheckRPMSaaSAccountUser();

  return (
    <Drawer
      variant="permanent"
      className={clsx(classes.drawer, {
        [classes.drawerOpen]: open,
        [classes.drawerClose]: !open,
      })}
      classes={{
        paper: clsx({
          [classes.drawerOpen]: open,
          [classes.drawerClose]: !open,
        }),
      }}
      open={open}
    >
      <div className={classes.toolbar}></div>
      <List style={{ marginTop: "9px" }}>
        {/* <SideNavTab
          icon={<AssessmentIcon className={classes.button} />}
          title="Transmissions"
          route={`/${transmissionsRoute}`}
          showBadge={true}
          badgeCount={activeBadgeCount}
        /> */}

        <SideNavTab
          icon={<AssessmentIcon className={classes.button} />}
          title="Transmissions"
          route={transmissionsRoute}
          onClick={() => {
            removeCache();
          }}
          showBadge={true}
          badgeCount={activeCount}
          disabled={disableTab}
          showTab={!isRPMSaaSUser}
        />

        <SideNavTab
          icon={<FavoriteIcon className={classes.button} />}
          title="Remote Patient Monitoring"
          route={currentUser?.clinii_user_id ? "/rpm" : "/patient/rpm"}
          disabled={disableTab}
          showTab={
            isRPMSaaSUser ||
            [USER_ROLES.ADMIN, USER_ROLES.USER].includes(role) ||
            (role === USER_ROLES.CLINIC && hasRPMDevices) ||
            Boolean(currentUser?.clinii_user_id)
          }
        />

        <SideNavTab
          icon={<PeopleIcon className={classes.button} />}
          title="Patients"
          route={`/patients`}
          onClick={() => dispatch(changeFilters([]))}
          disabled={disableTab}
          showTab={!isRPMSaaSUser}
        />

        <SideNavTab
          icon={
            <FileAlertIcon
              style={{ marginLeft: "4px" }}
              className={classes.button}
            />
          }
          title="No Show Patients"
          route={`/no-show-patients`}
          disabled={disableTab}
          showTab={
            !isRPMSaaSUser && [USER_ROLES.ADMIN, USER_ROLES.USER].includes(role)
          }
        />

        <SideNavTab
          icon={<PieChartIcon className={classes.button} />}
          title="Dashboard"
          route={`/dashboard`}
          disabled={disableTab}
          showTab={!isRPMSaaSUser}
        />

        <SideNavTab
          icon={<NoteAddIcon className={classes.button} />}
          title="Tasks"
          route={`/tasks`}
          disabled={disableTab}
          showTab={
            !isRPMSaaSUser &&
            [
              USER_ROLES.ADMIN,
              USER_ROLES.USER,
              USER_ROLES.ASSISTANT,
              USER_ROLES.CLINIC_ADMIN,
            ].includes(currentUser?.role)
          }
        />

        <SideNavTab
          icon={<GppGoodIcon className={classes.button} />}
          title="Billing"
          route={`/billing/insurances`}
          baseRoute={`/billing`}
          disabled={disableTab}
          showTab={
            !isRPMSaaSUser && [USER_ROLES.ADMIN].includes(currentUser?.role)
          }
        />

        <SideNavTab
          icon={<PublishIcon className={classes.button} />}
          title="Upload Report"
          route={`/upload`}
          disabled={disableTab}
          showTab={
            !isRPMSaaSUser &&
            [
              ...new Set([
                USER_ROLES.ADMIN,
                USER_ROLES.USER,
                USER_ROLES.CLINIC_ADMIN,
                ...(CLINIC_ROLES.includes(currentUser?.role)
                  ? getRolePreferenceByAccount(
                      accounts,
                      clinicName,
                      "manual_upload",
                      "report_upload"
                    )
                  : []),
              ]),
            ].includes(currentUser?.role)
          }
        />

        <SideNavTab
          icon={<SettingsIcon className={classes.button} />}
          title="Settings"
          route={`/settings`}
          disabled={disableTab}
          showTab={
            isRPMSaaSUser ||
            [
              USER_ROLES.ADMIN,
              USER_ROLES.CLINIC_ADMIN,
              USER_ROLES.RPM_USER,
            ].includes(currentUser?.role) ||
            hasSettingsAccess
          }
        />

        <SideNavTab
          icon={<AssignmentIcon className={classes.button} />}
          title="Reports Assignment"
          route={`/reports-assignment`}
          disabled={disableTab}
          showTab={
            !isRPMSaaSUser &&
            [CLINIC_NAMES.CENTRA].includes(clinicName) &&
            role === USER_ROLES.CLINIC
          }
        />

        <SideNavTab
          icon={<TableChartIcon className={classes.button} />}
          title="EHR Jobs"
          route={`/ehr-jobs/hl7Jobs`}
          disabled={disableTab}
          showTab={
            !isRPMSaaSUser &&
            [USER_ROLES.ADMIN, USER_ROLES.CLINIC_ADMIN].includes(
              currentUser?.role
            )
          }
        />

        <SideNavTab
          icon={<ReportTemplateIcon className={classes.button} />}
          title="Report Templates"
          route={`/report-templates`}
          disabled={disableTab}
          showTab={
            !isRPMSaaSUser &&
            [
              USER_ROLES.ADMIN,
              USER_ROLES.USER,
              USER_ROLES.ASSISTANT,
              USER_ROLES.CLINIC_ADMIN,
            ].includes(currentUser?.role)
          }
        />

        <SideNavTab
          icon={<SignedReportIcon className={classes.button} />}
          title="Archived Reports"
          route={`/archived-reports`}
          disabled={disableTab}
          showTab={
            !isRPMSaaSUser &&
            [
              USER_ROLES.ADMIN,
              USER_ROLES.CLINIC_ADMIN,
              USER_ROLES.CLINIC,
              USER_ROLES.PHYSICIAN,
            ].includes(currentUser?.role)
          }
        />

        <SideNavTab
          icon={<BillingIcon className={classes.button} />}
          title="Billing Amount"
          route={`/billing-amount`}
          disabled={disableTab}
          showTab={
            !isRPMSaaSUser &&
            [USER_ROLES.ADMIN, USER_ROLES.CLINIC_ADMIN].includes(
              currentUser?.role
            )
          }
        />

        <SideNavTab
          icon={<WearableIcon className={classes.button} />}
          title="CardioOneConnect"
          route="/wearables"
          disabled={disableTab}
          showTab={
            !isRPMSaaSUser &&
            currentUser?.idp_user &&
            currentUser?.login_by === "cognito" &&
            Boolean(accountsMapper?.[clinicName]?.medical_center_id) &&
            [USER_ROLES.PHYSICIAN].includes(currentUser?.primary_role)
          }
        />

        <SideNavTab
          icon={<ManageAccountsIcon className={classes.button} />}
          title="Account Configurations"
          route={`/account-configurations/preference`}
          baseRoute={`/account-configurations`}
          disabled={disableTab}
          showTab={
            !isRPMSaaSUser && [USER_ROLES.ADMIN].includes(currentUser?.role)
          }
        />
      </List>
    </Drawer>
  );
}
